import { useId, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactModal from 'react-modal';
import { Link } from 'react-router-dom';
import { IoIosClose } from 'react-icons/io';

import Button from 'components/ui/button';
import { NotificationDetail, ctaFrom, interpolate } from 'pages/Notificacoes/notification-inbox';
import { extractEmailCard } from 'utils/extractEmailCard';
import { CTA_SLOT, markCtaSlot } from 'utils/markCtaSlot';
import { pruneEmailChrome } from 'utils/pruneEmailChrome';
import { sanitizeHtml } from 'utils/sanitizeHtml';

interface NotificationDetailModalProps {
  detail: NotificationDetail | null;
  loading: boolean;
  onClose: () => void;
}

/**
 * O aviso aberto renderiza a peça do e-mail **podada dos blocos que só valem numa
 * caixa de entrada** (CENTRAL-04.2, emendada na Fase 5).
 *
 * Antes da Fase 5 a peça vinha inteira, e dentro do app ela mentia: dizia "não é
 * necessário respondê-lo" e "você recebeu este e-mail" para quem não recebeu
 * e-mail nenhum, e oferecia "copie e cole este link no navegador" a quem já está
 * no navegador. Quem decide o que sai é o template, pelo `data-inapp="hide"`.
 *
 * A ordem das seis operações não é arbitrária:
 *
 * 1. **Interpolar** antes de sanear — sanear primeiro deixaria as variáveis
 *    entrarem no HTML já filtrado, sem passar pelo filtro. Desde a Fase 3 o corpo
 *    do comunicado vem de um editor, então isso deixou de ser teórico.
 * 2. **Sanear** antes de podar — a poda é cosmética e a barreira de segurança não
 *    deve depender dela. Se o marcador sumir do template, o pior caso é rodapé
 *    feio; nunca HTML não saneado.
 * 3. **Marcar o encaixe** do botão do app, no lugar do botão do e-mail — antes da
 *    poda, que é quem remove esse bloco.
 * 4. **Podar**, que encontra os nós por `data-inapp` — atributo que o DOMPurify
 *    preserva por padrão (provado em `sanitizeHtml.test.ts`).
 * 5. **Extrair o cartão** — o template embrulha o cartão numa tela de fundo de
 *    e-mail que, no app, virava uma segunda moldura (`extractEmailCard`).
 * 6. **Injetar**.
 *
 * O botão do e-mail é um dos blocos podados, e o CTA do modal o substitui:
 * `<a>` injetado por `dangerouslySetInnerHTML` nunca vira `<Link>`, então clicar
 * nele recarregaria a SPA inteira. O botão do modal navega por rota.
 *
 * Ao contrário do regulamento, que chega sem classes e precisa da tipografia do
 * `regulamento-content/styles.scss`, a peça de e-mail traz estilo 100% inline: o
 * contêiner não precisa de nada além de não atrapalhar. Por isso o modal não tem
 * moldura: o cartão da peça é o próprio modal, o fechar fica sobre o canto do
 * banner e quem rola é a tela, não uma caixa dentro dela. O botão de destino
 * entra por portal no lugar do botão do e-mail (`markCtaSlot`). Sem peça, um
 * cartão nas cores do template segura título, texto do catálogo e botão.
 */
export function NotificationDetailModal({ detail, loading, onClose }: NotificationDetailModalProps) {
  const titleId = useId();
  const [pecaEl, setPecaEl] = useState<HTMLDivElement | null>(null);
  const [encaixe, setEncaixe] = useState<Element | null>(null);

  const inapp = detail?.inapp;
  const cta = ctaFrom(inapp);

  /** Título: o do catálogo quando existe; senão o assunto do próprio e-mail. */
  let titulo = detail?.subject ?? 'Aviso';
  if (inapp && detail) titulo = interpolate(inapp.titulo, detail.vars);

  const peca = detail?.content;

  // Memorizado para o React não reinjetar o HTML a cada render e soltar o
  // encaixe de baixo do portal.
  const pecaHtml = useMemo(() => {
    if (!peca || !detail) return '';

    return extractEmailCard(pruneEmailChrome(markCtaSlot(sanitizeHtml(interpolate(peca, detail.vars)))));
  }, [peca, detail]);

  useLayoutEffect(() => {
    setEncaixe(pecaEl?.querySelector(`[${CTA_SLOT}]`) ?? null);
  }, [pecaEl, pecaHtml]);

  const botao = cta && (
    <div className="flex justify-center px-8 pt-4 pb-6">
      {cta.url.startsWith('/') && (
        <Link to={cta.url} onClick={onClose}>
          <Button color="clear" className="leading-none p-2 bg-yellow text-blue-dark">
            {cta.label}
          </Button>
        </Link>
      )}
      {!cta.url.startsWith('/') && (
        <a href={cta.url} target="_blank" rel="noopener noreferrer">
          <Button color="clear" className="leading-none p-2 bg-yellow text-blue-dark">
            {cta.label}
          </Button>
        </a>
      )}
    </div>
  );

  return (
    <ReactModal
      isOpen={loading || !!detail}
      onRequestClose={onClose}
      contentLabel="Detalhe da notificação"
      aria={{ labelledby: titleId }}
      portalClassName="relative z-50"
      overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-60 flex flex-col items-center w-screen h-screen p-6 py-[8vh] overflow-y-auto"
      className="outline-none w-full max-w-[600px]"
      shouldReturnFocusAfterClose={true}
    >
      <div className={peca ? 'relative' : 'relative rounded-2xl bg-[#0C2648] border border-[#2E7C8A]'}>
        <button
          type="button"
          className="absolute right-3 top-3 z-10 rounded-full bg-black bg-opacity-40 w-9 h-9 p-0 flex justify-center items-center hover:bg-opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
          onClick={onClose}
          aria-label="Fechar notificação"
        >
          <IoIosClose color="white" size={32} aria-hidden="true" />
        </button>

        {/* A peça já traz banner e tagline: o título só aparece quando não há peça. */}
        <h2 id={titleId} className={peca || loading ? 'sr-only' : 'text-white text-xl lg:text-2xl px-6 pt-6 pr-14'}>
          {titulo}
        </h2>

        {loading && (
          <div role="status" aria-live="polite" className="py-8 text-center text-white">
            <span className="sr-only">Carregando notificação</span>
            <span aria-hidden="true">Carregando...</span>
          </div>
        )}

        {/* O cartão do template tem 600px fixos; aqui ele acompanha a largura do modal. */}
        {!loading && pecaHtml && (
          <div ref={setPecaEl} className="[&>*]:!w-full" dangerouslySetInnerHTML={{ __html: pecaHtml }} />
        )}

        {/* Template ausente: o aviso não some, cai para o texto do catálogo. */}
        {!loading && detail && !peca && inapp && (
          <p className="font-thin text-white px-6 py-4">{interpolate(inapp.texto, detail.vars)}</p>
        )}

        {/* O botão ocupa o lugar do botão do e-mail; sem encaixe, fecha o cartão. */}
        {!loading && botao && encaixe && createPortal(botao, encaixe)}
        {!loading && botao && !encaixe && !peca && botao}
      </div>

      {/* Template antigo, sem encaixe: o botão vem logo abaixo da peça. */}
      {!loading && botao && !encaixe && peca && botao}
    </ReactModal>
  );
}
